/**
 * PWA Install Prompt
 * Shows install button for Progressive Web App
 */

import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const InstallPWA = ({ className = "" }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      // Show iOS instructions
      if (isIOS) {
        setShowInstallDialog(true);
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  // Show install button only if prompt is available or on iOS
  if (!deferredPrompt && !isIOS) {
    return null;
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => isIOS ? setShowInstallDialog(true) : handleInstall()}
        className={`text-zinc-400 hover:text-emerald-400 h-8 w-8 md:h-10 md:w-10 p-0 ${className}`}
        data-testid="install-pwa-btn"
      >
        <Download className="w-5 h-5 md:w-6 md:h-6" />
      </Button>

      <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Smartphone className="w-5 h-5 text-emerald-400" />
              Instalar CENTINELA
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Instala la aplicación en tu dispositivo para acceso rápido
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {isIOS ? (
              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sm text-zinc-200">Instrucciones para iOS:</h4>
                <ol className="space-y-2 text-sm text-zinc-400 list-decimal list-inside">
                  <li>Toca el botón <strong>Compartir</strong> (icono de cuadrado con flecha)</li>
                  <li>Desplázate y selecciona <strong>"Añadir a pantalla de inicio"</strong></li>
                  <li>Toca <strong>"Añadir"</strong> en la esquina superior derecha</li>
                </ol>
              </div>
            ) : (
              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sm text-zinc-200">Beneficios:</h4>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Acceso rápido desde tu pantalla de inicio
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Funciona sin conexión para registrar vaciados
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Recibe notificaciones de alertas críticas
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowInstallDialog(false)}
              className="text-zinc-400"
            >
              Cerrar
            </Button>
            {!isIOS && deferredPrompt && (
              <Button
                onClick={handleInstall}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Instalar Ahora
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallPWA;
